export const SITE_URL =
  (import.meta.env.VITE_SITE_URL as string) || 'https://cendien.com';

export function absoluteUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Fixed dimensions/alt for the default Open Graph image. */
export const OG_IMAGE_WIDTH = '1200';
export const OG_IMAGE_HEIGHT = '630';
export const OG_IMAGE_ALT = 'Cendien - IT Consulting, ERP & AI Solutions';

/**
 * Site-wide default Open Graph image (1.2:1.99 … exactly 1200×630). Stored as
 * an absolute URL so it is valid no matter which domain serves the site.
 */
export const DEFAULT_OG_IMAGE =
  'https://static.readdy.ai/image/8e0505aa0635a0a18473d6b2a0ce03ab/8c3017681cf77fd83f88325a075b6a56.jpeg';

/** Browser origin at runtime; the configured canonical origin during SSR/build. */
export function siteOrigin(): string {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  return SITE_URL;
}

/**
 * Resolves an OG image value to an absolute URL.
 * - absolute (https://… or //…) → returned unchanged
 * - site-relative (/og-image.jpg) → joined with the current origin (or `origin`)
 * - empty → the site-wide default
 */
export function resolveOgImage(value?: string, origin?: string): string {
  const raw = (value || '').trim();
  if (!raw) return DEFAULT_OG_IMAGE;
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith('//')) return `https:${raw}`;
  const base = (origin || siteOrigin()).replace(/\/+$/, '');
  return `${base}${raw.startsWith('/') ? raw : `/${raw}`}`;
}

/**
 * Routes register their own OG image here (keyed by pathname) so the global
 * useSEO pass keeps a page-specific image instead of overwriting it with the
 * default. Passing an empty value clears the entry.
 */
const pageOgImages = new Map<string, string>();

export function registerPageOgImage(path: string, url?: string): void {
  const clean = (url || '').trim();
  if (clean) pageOgImages.set(path, clean);
  else pageOgImages.delete(path);
}

export function getPageOgImage(path: string): string | undefined {
  return pageOgImages.get(path);
}

/** Normalises a route path for comparison (trailing slash aside, '/' stays '/'). */
function normalizePath(path: string): string {
  return (path || '').replace(/\/+$/, '') || '/';
}

/**
 * The selector for JSON-LD blocks that came from the prerendered HTML. The
 * build marks them `data-prerendered="1"` (the legacy `data-prerender-jsonld`
 * attribute is still recognised so an older cached shell keeps working).
 * `data-seo-jsonld` carries the same stable id the runtime uses, so the client
 * can update these tags in place instead of injecting duplicate copies.
 */
const PRERENDERED_JSONLD_SELECTOR =
  'script[data-prerendered="1"], script[data-prerender-jsonld="1"]';

/**
 * True when the current document was prerendered for this exact path AND still
 * carries the static JSON-LD blocks. In that case the client shell must NOT
 * overwrite the route's <title>/description/OG tags — those come from the
 * static HTML. As soon as the user navigates client-side (or the static blocks
 * are removed), this returns false so the normal runtime head pass resumes.
 * JSON-LD itself is always reconciled (in place) by the callers.
 */
export function isPrerenderedFor(path: string): boolean {
  if (typeof document === 'undefined') return false;
  const meta = document.head.querySelector('meta[name="x-prerendered-path"]');
  if (!meta) return false;
  if (normalizePath(meta.getAttribute('content') || '') !== normalizePath(path)) return false;
  return !!document.head.querySelector(
    `script[type="application/ld+json"][data-prerendered="1"], script[type="application/ld+json"][data-prerender-jsonld="1"]`,
  );
}

/** Drops the static JSON-LD blocks once the client takes over the head. */
export function removePrerenderedJsonLd(): void {
  if (typeof document === 'undefined') return;
  document.head.querySelectorAll(PRERENDERED_JSONLD_SELECTOR).forEach((s) => s.remove());
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export interface HeadMetaOptions {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  ogImage?: string;
  type?: string;
}

/** Sets a unique <title>, meta description/keywords, canonical, and social tags. */
export function applyHeadMeta(opts: HeadMetaOptions) {
  const url = absoluteUrl(opts.path);
  document.title = opts.title;
  upsertMeta('name', 'description', opts.description);
  if (opts.keywords) upsertMeta('name', 'keywords', opts.keywords);
  upsertCanonical(url);

  upsertMeta('property', 'og:title', opts.title);
  upsertMeta('property', 'og:description', opts.description);
  upsertMeta('property', 'og:url', url);
  upsertMeta('property', 'og:type', opts.type || 'website');
  const ogImage = resolveOgImage(opts.ogImage);
  upsertMeta('property', 'og:image', ogImage);
  upsertMeta('property', 'og:image:width', OG_IMAGE_WIDTH);
  upsertMeta('property', 'og:image:height', OG_IMAGE_HEIGHT);
  upsertMeta('property', 'og:image:alt', OG_IMAGE_ALT);
  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', opts.title);
  upsertMeta('name', 'twitter:description', opts.description);
  upsertMeta('name', 'twitter:image', ogImage);
  upsertMeta('name', 'twitter:image:alt', OG_IMAGE_ALT);
}

/**
 * Insert or update a single JSON-LD block identified by a stable id.
 *
 * On a prerendered page the static HTML already ships a block with the SAME id
 * (marked data-prerendered="1"), so this finds it and rewrites its contents in
 * place — guaranteeing exactly one block per @type after hydration. Any stray
 * duplicate with the same id (e.g. from a hot reload) is collapsed to one.
 */
export function upsertJsonLd(id: string, data: unknown) {
  const existing = document.head.querySelectorAll<HTMLScriptElement>(`script[data-seo-jsonld="${id}"]`);
  let el = existing[0];
  for (let i = 1; i < existing.length; i += 1) existing[i].remove();
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('data-seo-jsonld', id);
    document.head.appendChild(el);
  }
  el.setAttribute('type', 'application/ld+json');
  el.textContent = JSON.stringify(data, null, 0);
}

export function removeJsonLd(id: string) {
  document.head.querySelectorAll(`script[data-seo-jsonld="${id}"]`).forEach((s) => s.remove());
}